import { styled } from "@mui/material/styles";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, Typography, Tabs, Tab, Button } from "@mui/material";

import DiagnosticReportTabPanel from "./DiagnosticReportTabPanel";
import GeneralView from "./GeneralView";
import ParentFaultView from "./ParentFaultViews";
import { SweepArgument, SweepArguments, MetaArguments } from "../../views/GeneralTask/generalTask.types";
import { MetaToolTip } from "../common/MetaToolTip";
import FlipChartControls from "./FlipChartControls";
import NamedFaultsView from "./NamedFaultsView";
import DisableHotkeyContext from "../../contexts/DisableHotkeyContext";
import { InversionSolutionDiagnosticContainerQuery$data } from "../../views/GeneralTask/__generated__/InversionSolutionDiagnosticContainerQuery.graphql";
import { filterMetaArguments, filteredMetaGT } from "./diagnosticReportCard.service";
import { viewPath } from "../../utils/routeUtils";
import { useMfdTableMeta } from "../common/MfdChart";

const Container = styled("div")(() => ({
  flexGrow: 1,
}));

interface DiagnosticReportCardProps {
  inversionSolutions: InversionSolutionDiagnosticContainerQuery$data;
  sweepArgs?: SweepArguments;
  sweepList?: string[];
  modelType: string;
  changeCurrentImage?: (index: number) => void;
  reportTab?: number;
  setReportTab?: (tab: number) => void;
}

const DiagnosticReportCard: React.FC<DiagnosticReportCardProps> = ({
  sweepArgs,
  sweepList,
  modelType,
  inversionSolutions,
  changeCurrentImage,
  reportTab,
  setReportTab,
}: DiagnosticReportCardProps) => {
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [filteredMeta, setFilteredMeta] = useState<MetaArguments>([]);
  const { disableHotkey, setDisableHotkey } = useContext(DisableHotkeyContext);

  const solutionList = inversionSolutions?.nodes?.result?.edges || [];

  useEffect(() => {
    setCurrentImage(0);
  }, [inversionSolutions]);

  useEffect(() => {
    if (reportTab !== 0) setCurrentTab(reportTab ?? 0);
  }, [reportTab]);

  useEffect(() => {
    if (setReportTab) {
      setReportTab(currentTab);
    }
  }, [setReportTab, currentTab]);

  useEffect(() => {
    if (solutionList[currentImage]) {
      let metaList: MetaArguments = [];

      if (sweepArgs) {
        metaList = filteredMetaGT(solutionList[currentImage]?.node?.meta, sweepArgs);
      } else if (sweepList) {
        metaList = filterMetaArguments(solutionList[currentImage]?.node?.meta, sweepList);
      }

      if (
        inversionSolutions?.nodes?.result?.edges[currentImage]?.node?.__typename === "ScaledInversionSolution" ||
        (inversionSolutions?.nodes?.result?.edges[currentImage]?.node?.__typename ===
          "TimeDependentInversionSolution" &&
          solutionList[currentImage]?.node?.meta !== undefined)
      ) {
        const sourceMeta = solutionList[currentImage]?.node?.meta;
        const sweepArgs: (SweepArgument | null)[] = [];
        sourceMeta?.map((kv) => {
          if (kv?.v && kv?.v.includes("tag")) {
            sweepArgs.push(kv as SweepArgument);
          }
        });
        if (sourceMeta !== undefined) {
          metaList = filteredMetaGT(sourceMeta, sweepArgs as SweepArguments);
        }
      }
      setFilteredMeta(metaList);
    }
  }, [currentImage, sweepList, sweepArgs, solutionList]);

  const mfdTableMeta = useMfdTableMeta(solutionList[currentImage]);

  const nextImage = () => {
    if (currentImage < solutionList.length - 1) {
      setCurrentImage(currentImage + 1);
      if (changeCurrentImage) {
        changeCurrentImage(currentImage + 1);
      }
    }
  };

  const prevImage = () => {
    if (currentImage > 0) {
      setCurrentImage(currentImage - 1);
      if (changeCurrentImage) {
        changeCurrentImage(currentImage - 1);
      }
    }
  };

  const handleTabChange = (_event: React.ChangeEvent<object>, newValue: number) => {
    setCurrentTab(newValue);
  };

  const hotkeyHandler = (event: KeyboardEvent) => {
    if (disableHotkey === false) {
      if (event.key === ">" || event.key === "." || event.key === "ArrowRight") nextImage();
      if (event.key === "<" || event.key === "," || event.key === "ArrowLeft") prevImage();
    }
  };

  useEffect(() => {
    window.addEventListener("keyup", hotkeyHandler);
    return () => window.removeEventListener("keyup", hotkeyHandler);
  });

  if (!solutionList[currentImage]) {
    return <Typography> There are no valid reports to show. </Typography>;
  }

  const renderTab = () => {
    switch (currentTab) {
      default:
        return (
          <DiagnosticReportTabPanel value={currentTab} index={0}>
            <GeneralView
              id={solutionList[currentImage]?.node?.id || ""}
              mfdTableMeta={mfdTableMeta}
              filteredMeta={filteredMeta}
              currentImage={currentImage}
              automationTasksLength={solutionList.length}
              solutionType={solutionList[currentImage]?.node?.__typename || ""}
            />
          </DiagnosticReportTabPanel>
        );

      case 1:
        return (
          <DiagnosticReportTabPanel value={currentTab} index={1}>
            <ParentFaultView id={solutionList[currentImage]?.node?.id || ""} setDisableHotkey={setDisableHotkey} />
          </DiagnosticReportTabPanel>
        );
      case 2:
        return (
          <DiagnosticReportTabPanel value={currentTab} index={2}>
            <NamedFaultsView id={solutionList[currentImage]?.node?.id || ""} />
          </DiagnosticReportTabPanel>
        );
    }
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <h4>
            {solutionList[currentImage]?.node?.__typename}
            :&nbsp;
            {solutionList[currentImage]?.node?.id || ""}&nbsp;&nbsp;&nbsp;
            <Button component={Link} to={viewPath(solutionList[currentImage]?.node?.id)}>
              more
            </Button>
          </h4>
          <MetaToolTip meta={filteredMeta} />
          <FlipChartControls
            id={solutionList[currentImage]?.node?.id || ""}
            currentImage={currentImage}
            handlePrev={prevImage}
            handleNext={nextImage}
            totalLength={solutionList.length}
          />
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="General" id="simple-tab-0" disableFocusRipple />
            <Tab
              label="Parent Faults"
              id="simple-tab-1"
              disabled={
                modelType !== "CRUSTAL" || solutionList[currentImage]?.node?.__typename === "ScaledInversionSolution"
              }
              disableFocusRipple
            />
            <Tab
              label="Named Faults"
              disabled={
                modelType !== "CRUSTAL" || solutionList[currentImage]?.node?.__typename === "ScaledInversionSolution"
              }
              id="simple-tab-2"
              disableFocusRipple
            />
          </Tabs>
          {renderTab()}
        </CardContent>
      </Card>
    </Container>
  );
};

export default DiagnosticReportCard;
